import { useCallback, useEffect, useMemo, useState } from 'react'
import { message } from 'antd'
import { useMutation, useQueries, useQuery, useQueryClient } from 'react-query'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'

import { resolveWarehouseName, resolveWarehouseParentId } from 'utils/warehouseName'
import {
  addFinanceWarehouseContactGroups,
  addFinanceWarehouseRoles,
  addFinanceWareHouseTransfer,
  addFinanceWareHouseTransferGroup,
  approveWarehouseTransfer,
  approveWareHouseTransferGroup,
  archiveFinanceWareHouse,
  deleteFinanceWareHouse,
  deleteFinanceWareHouseTransfer,
  deleteFinanceWareHouseTransferGroup,
  deleteOnAddFinanceWareHouseTransferAttachment,
  editFinanceWareHouse,
  editFinanceWareHouseTransferGroup,
  executeImportFinanceWarehouseTransfer,
  getFinanceWareHouse,
  getFinanceWarehouseRoles,
  getFinanceWareHouses,
  getFinanceWareHouseTransactions,
  getFinanceWareHouseTransfer,
  getFinanceWareHouseTransferGroup,
  getFinanceWareHouseTransferGroups,
  getFinanceWareHouseTransfers,
  getRacks,
  getWarehousePinnedProducts,
  getWarehousesSummary,
  getWarehouseSummaryById,
  pinWarehouseProducts,
  rejectWarehouseTransfer,
  rejectWareHouseTransferGroup,
  revertWarehouseTransfer,
  revertWareHouseTransferGroup,
  unarchiveFinanceWareHouse,
  unpinWarehouseProduct,
  uploadImportFinanceWarehouseTransfer,
} from 'utils/apis'
import {
  executeMassDeleteStockAdjustment,
  getFinanceStockAdjustments,
  inputMassDeleteStockAdjustment,
} from 'utils/apis/financeStockAdjustment'
import {
  addFinanceWareHouse,
  addFinanceWareHouseRack,
  deleteFinanceWareHouseRack,
  editFinanceWareHouseRack,
  moveFinanceWareHouse,
} from 'app-redux/financeWareHouses/actions'
import { warehousesSelector } from 'app-redux/selectors'

export const useWarehouseSummary = (id, options = {}) => {
  return useQuery(
    ['warehouseSummary', id],
    async () => {
      const {
        data: { data },
      } = await getWarehouseSummaryById(id)
      return data
    },
    {
      enabled: !!id,
      staleTime: 60_000,
      onError: (error) =>
        message.error((error && error.message) || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export const useFinanceWareHouseTransfers = (params = {}, options = {}) => {
  return useQuery(
    ['warehousesTransfer', params],
    async () => {
      let response

      try {
        const {
          data: { data },
        } = await getFinanceWareHouseTransfers(params)
        response = data
      } catch (error) {
        throw new Error('Failed to load data from server!')
      }
      return response
    },
    {
      onError: (error) => message.error(error?.message || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export const useUploadImportFinanceWarehouseTransferMutation = () => {
  return useMutation(uploadImportFinanceWarehouseTransfer)
}

export const useExecuteImportFinanceWarehouseTransferMutation = () => {
  return useMutation(executeImportFinanceWarehouseTransfer)
}

export const useAddWarehouseContactGroupsMutation = () => {
  return useMutation((payload) => addFinanceWarehouseContactGroups(payload.id, payload.data))
}

export const useWarehousesSummary = ({ params = {}, options = {} } = {}) => {
  return useQuery(
    ['warehousesSummary', params],
    async () => {
      let response

      try {
        const {
          data: { data },
        } = await getWarehousesSummary(params)
        response = data
      } catch (error) {
        throw new Error(error)
      }
      return response
    },
    {
      onError: (error) => message.error(error?.message || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export const useWarehouses = ({ payload, options = {} }) => {
  const { formatMessage } = useIntl()

  return useQuery(
    ['warehouses', payload],
    async () => {
      let response

      try {
        const {
          data: { data },
        } = await getFinanceWareHouses(payload)
        response = data
      } catch (error) {
        throw new Error(error)
      }
      return response
    },
    {
      onError: (error) =>
        message.error(
          error?.data?.message ||
            formatMessage({ id: 'financeWareHouses.error_cannot_load_warehouses' }),
        ),
      ...options,
    },
  )
}
export function useWarehouseById({ id, params = {}, options }) {
  return useQuery(
    ['warehouse', id, params],
    async () => {
      let response
      try {
        const {
          data: { data },
        } = await getFinanceWareHouse(id, params)
        response = data
      } catch (error) {
        throw new Error('Failed to load data from server!')
      }
      return response
    },
    options,
  )
}

export const useUpdateWarehouse = () => {
  return useMutation((payload) => editFinanceWareHouse(payload))
}
export const useDeleteWarehouse = () => {
  return useMutation(({ id }) => deleteFinanceWareHouse(id))
}
export const useArchiveWarehouse = () => {
  return useMutation(({ id }) => archiveFinanceWareHouse(id))
}
export const useUnarchiveWarehouse = () => {
  return useMutation(({ id }) => unarchiveFinanceWareHouse(id))
}

export const useWarehouseRoles = ({ id, options = {} }) => {
  return useQuery(
    ['warehouseRoles', id],
    async () => {
      const {
        data: { data },
      } = await getFinanceWarehouseRoles(id)
      return data
    },
    {
      onError: (error) => message.error(error?.data?.message || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export const useUpdateWarehouseRolesMutation = () => {
  return useMutation((payload) => addFinanceWarehouseRoles(payload.id, payload.data))
}

export const useWarehouseTransferById = ({ id, params = {}, options = {} }) => {
  return useQuery(
    ['warehouseTransfer', id, params],
    async () => {
      const {
        data: { data },
      } = await getFinanceWareHouseTransfer(id, params)
      return data
    },
    {
      onError: (error) => message.error(error?.data?.message || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export const useApproveWarehouseTransferMutation = () => {
  return useMutation(approveWarehouseTransfer, {
    onError: (error) => {
      message.error(error?.data?.message || 'Failed to approve warehouse transfer!')
    },
  })
}

export const useRejectWarehouseTransferMutation = () => {
  return useMutation(rejectWarehouseTransfer, {
    onError: (error) => {
      message.error(error?.data?.message || 'Failed to reject warehouse transfer!')
    },
  })
}

export const useRevertWarehouseTransferMutation = () => {
  return useMutation(revertWarehouseTransfer, {
    onError: (error) => {
      message.error(error?.data?.message || 'Failed to revert warehouse transfer!')
    },
  })
}

export const useDeleteWarehouseTransferMutation = () => {
  return useMutation(deleteFinanceWareHouseTransfer, {
    onError: (error) => {
      message.error(error?.data?.message || 'Failed to delete warehouse transfer!')
    },
  })
}

export function useParallelWarehouseTransferDetails({
  transferIds,
  payload = {},
  enabled = false,
  batchSize = 15,
}) {
  // TODO: MAKE REUSABLE batch
  const [currentBatchIndex, setCurrentBatchIndex] = useState(0)
  const [completedBatches, setCompletedBatches] = useState(new Set())

  const batches = useMemo(() => {
    const totalChunks = Math.ceil(transferIds.length / batchSize)
    return Array.from({ length: totalChunks }, (_, index) => {
      return transferIds.slice(index * batchSize, (index + 1) * batchSize)
    })
  }, [batchSize, transferIds])

  const transferIdsKey = useMemo(() => transferIds.join(','), [transferIds])

  useEffect(() => {
    setCurrentBatchIndex(0)
    setCompletedBatches(new Set())
  }, [transferIdsKey, enabled])

  const queries = useQueries(
    transferIds.map((id, idx) => {
      const batchIndex = Math.floor(idx / batchSize)
      const isInCurrentBatch = batchIndex === currentBatchIndex
      const isBatchCompleted = completedBatches.has(batchIndex)

      return {
        queryKey: ['warehouseTransfer', id, payload],
        queryFn: async () => {
          const {
            data: { data },
          } = await getFinanceWareHouseTransfer(id, payload)
          return data
        },
        enabled: !!id && enabled && (isBatchCompleted || isInCurrentBatch),
        onError: (error) =>
          message.error(error?.data?.message || 'Failed to load data from server!'),
      }
    }),
  )

  useEffect(() => {
    if (!enabled || batches.length === 0 || currentBatchIndex >= batches.length) {
      return
    }

    const currentBatch = batches[currentBatchIndex]
    if (!currentBatch) {
      return
    }

    const startIndex = currentBatchIndex * batchSize
    const endIndex = startIndex + currentBatch.length
    const currentBatchQueries = queries.slice(startIndex, endIndex)

    const allCompleted = currentBatchQueries.every(
      (query) => !query.isLoading && !query.isFetching && (query.isSuccess || query.isError),
    )

    if (allCompleted) {
      setCompletedBatches((prev) => new Set([...prev, currentBatchIndex]))

      if (currentBatchIndex <= batches.length - 1) {
        setCurrentBatchIndex((prev) => prev + 1)
      }
    }
  }, [queries, currentBatchIndex, batches, batchSize, enabled])

  return queries
}

export const useWareHouseTransferGroups = (params = {}, options = {}) => {
  return useQuery(
    ['warehouseTransferGroups', params],
    async () => {
      const {
        data: { data },
      } = await getFinanceWareHouseTransferGroups(params)
      return data
    },
    {
      onError: (error) => message.error(error?.data?.message || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export const useWareHouseTransferGroupById = ({ id, params = {}, options = {} }) => {
  return useQuery(
    ['warehouseTransferGroup', id, params],
    async () => {
      const {
        data: { data },
      } = await getFinanceWareHouseTransferGroup(id, params)
      return data
    },
    {
      onError: (error) => message.error(error?.data?.message || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export const useApproveWareHouseTransferGroupMutation = () => {
  return useMutation(approveWareHouseTransferGroup, {
    onError: (error) => {
      message.error(error?.data?.message || 'Failed to approve warehouse transfer group!')
    },
  })
}

export const useRejectWareHouseTransferGroupMutation = () => {
  return useMutation(rejectWareHouseTransferGroup, {
    onError: (error) => {
      message.error(error?.data?.message || 'Failed to reject warehouse transfer group!')
    },
  })
}

export const useRevertWareHouseTransferGroupMutation = () => {
  return useMutation(revertWareHouseTransferGroup, {
    onError: (error) => {
      message.error(error?.data?.message || 'Failed to revert warehouse transfer group!')
    },
  })
}

// Tanpa `onError` di sini: notifikasi global sudah dibungkam di service, dan container
// yang memegang form yang menampilkan pesannya (error 422 ke field, error lain apa adanya).
export const useUpdateWareHouseTransferGroupMutation = () => {
  return useMutation(editFinanceWareHouseTransferGroup)
}

export const useDeleteWareHouseTransferGroupMutation = () => {
  return useMutation(deleteFinanceWareHouseTransferGroup, {
    onError: (error) => {
      message.error(error?.data?.message || 'Failed to delete warehouse transfer group!')
    },
  })
}

export const useAddWarehouseTransferMutation = () => {
  return useMutation(addFinanceWareHouseTransfer)
}

// Lihat catatan di `useUpdateWareHouseTransferGroupMutation` soal error ditangani container.
export const useAddWareHouseTransferGroupMutation = () => {
  return useMutation(addFinanceWareHouseTransferGroup)
}

export const useDeleteWarehouseTransferAttachmentMutation = () => {
  return useMutation(deleteOnAddFinanceWareHouseTransferAttachment)
}

export const useFinanceWareHouseTransactions = ({ id, params = {}, options = {} } = {}) => {
  return useQuery(
    ['warehouseTransactions', id, params],
    async () => {
      const {
        data: { data },
      } = await getFinanceWareHouseTransactions(id, params)
      return data
    },
    {
      onError: (error) => message.error(error?.message || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export const useFinanceStockAdjustments = ({ params = {}, options = {} } = {}) => {
  return useQuery(
    ['stockAdjustments', params],
    async () => {
      const {
        data: { data },
      } = await getFinanceStockAdjustments(params)
      return data
    },
    {
      onError: (error) => message.error(error?.message || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export const useInputMassDeleteStockAdjustmentMutation = () => {
  return useMutation(inputMassDeleteStockAdjustment)
}

export const useExecuteMassDeleteStockAdjustmentMutation = () => {
  return useMutation(executeMassDeleteStockAdjustment)
}

export const useAddWarehouseMutation = () => {
  const dispatch = useDispatch()
  return useMutation((payload) => dispatch(addFinanceWareHouse(payload)))
}

export const useMoveWarehouseMutation = () => {
  const dispatch = useDispatch()
  return useMutation((payload) => dispatch(moveFinanceWareHouse(payload)))
}

export const useRacksQuery = ({ id, options = {} } = {}) => {
  return useQuery(
    ['warehouseRacks', id],
    async () => {
      const {
        data: { data },
      } = await getRacks(id)
      return data
    },
    {
      enabled: Boolean(id),
      onError: (error) => message.error(error?.data?.message || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export const useAddRackMutation = () => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  return useMutation((payload) => dispatch(addFinanceWareHouseRack(payload)), {
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries(['warehouseRacks', id])
    },
  })
}

export const useUpdateRackMutation = () => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  return useMutation((payload) => dispatch(editFinanceWareHouseRack(payload)), {
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries(['warehouseRacks', id])
    },
  })
}

export const useDeleteRackMutation = () => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  return useMutation((payload) => dispatch(deleteFinanceWareHouseRack(payload)), {
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries(['warehouseRacks', id])
    },
  })
}

// Nama gudang rak-aware ("Induk - Rak") untuk tampilan read-only. Redux memuat induk + rak, jadi
// resolusi sinkron dari selector: nama tampil di render pertama tanpa request tambahan.
export const useWarehouseNameResolver = () => {
  const warehouses = useSelector(warehousesSelector)

  return useCallback((id) => resolveWarehouseName(warehouses, id), [warehouses])
}

// Rak dipetakan ke induknya untuk atribut yang hanya dimiliki induk. `rows` tetap dioper pemanggil
// agar ia bisa memilih sumber yang sama dengan yang ia baca field-nya.
export const useWarehouseParentIdResolver = (rows) => {
  return useCallback((id) => resolveWarehouseParentId(rows, id), [rows])
}

export const useWarehousePinnedProducts = ({ id, options = {} } = {}) => {
  return useQuery(
    ['warehousePinnedProducts', id],
    async () => {
      const {
        data: { data },
      } = await getWarehousePinnedProducts(id)
      return data
    },
    {
      enabled: !!id,
      onError: (error) => message.error(error?.data?.message || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export const usePinWarehouseProductsMutation = () => {
  const queryClient = useQueryClient()
  return useMutation(
    ({ id, productIds }) => pinWarehouseProducts(id, { product_ids: productIds }),
    {
      onSuccess: (_res, { id }) => {
        queryClient.invalidateQueries(['warehouse', id])
      },
    },
  )
}

export const useUnpinWarehouseProductsMutation = () => {
  const queryClient = useQueryClient()
  return useMutation(
    // Tidak ada bulk DELETE → loop DELETE per produk, kumpulkan hasilnya
    async ({ id, productIds }) => {
      const results = await Promise.allSettled(
        productIds.map((productId) => unpinWarehouseProduct(id, productId)),
      )
      // Kumpulkan id yang gagal supaya seleksi bisa disisakan untuk retry
      const failedProductIds = productIds.filter(
        (_productId, index) => results[index].status === 'rejected',
      )
      return {
        success: productIds.length - failedProductIds.length,
        failed: failedProductIds.length,
        failedProductIds,
      }
    },
    {
      onSuccess: ({ success }, { id }) => {
        // Refetch hanya saat ada yang sukses supaya kolom & badge sinkron
        if (success > 0) {
          queryClient.invalidateQueries(['warehouse', id])
        }
      },
    },
  )
}
